import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import Marquee from 'react-fast-marquee';
import { ArrowRight, MessageCircle, ShieldCheck, Monitor, Tag, Tv, Users, Plane, Scale, LayoutDashboard, BarChart3, Gauge, Zap, ChevronRight, Quote, Play, ExternalLink, Search, CreditCard, Briefcase, ShoppingBag, UtensilsCrossed, Landmark, Heart, Mountain, Building, Wallet, Package, Cpu, ScanLine, Target, TrendingUp, Clock, Activity, MapPin } from 'lucide-react';
import { getMetrics, getLogos, getSolutions, getCases } from '@/lib/api';
import { useContentList } from '@/lib/useContent';
import { fadeUp, stagger, fadeIn } from '@/lib/motion';
import { Section, SectionTitle, AnimatedCard, AuthorityBadge } from '@/components/shared/Section';
import { MiniForm } from '@/components/shared/Forms';
import QRDemoSection from '@/components/shared/QRDemoSection';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePrice } from '@/lib/priceMode';
import OffersBlock from '@/components/home/OffersBlock';
import HeroBlock from '@/components/home/HeroBlock';
import IndustriesBlock from '@/components/home/IndustriesBlock';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const solutionIcons = {
  'autoservicio-self-checkout': Monitor,
  'esl-precios-digitales': Tag,
  'carteleria-digital': Tv,
  'gestion-filas': Users,
  'turnomaticos-gestion-filas': Users,
  'aeropuertos': Plane,
  'pesaje-industrial': Scale,
  'balanzas-pesaje-comercial': Scale,
  'portillones-automaticos': ShieldCheck,
  'maquinas-vending': Package,
  'puntos-de-venta-pos': CreditCard,
};


const platformTabs = [
  { id: 'control-panel', label: 'Control Panel', icon: LayoutDashboard, image: '/control_panel_dashboard.webp', description: 'Panel de control centralizado para monitorear todos tus dispositivos en tiempo real. Supervisa kioscos, POS, balanzas, pantallas y equipos desde una vista unificada.', features: ['Vista unificada de todos tus dispositivos', 'Monitoreo en tiempo real 24/7', 'Alertas automáticas configurables', 'Reportes y analytics operacionales'] },
  { id: 'ceo-cockpit', label: 'CEO Cockpit', icon: Gauge, image: '/ceo_cockpit_dashboard.png', description: 'Vista ejecutiva para CEOs de retail: ventas por tienda, comparativos, ranking de sucursales y alertas de negocio en una sola pantalla.', features: ['KPIs de ventas por tienda', 'Ranking de sucursales', 'Alertas ejecutivas', 'Comparativos de período'] },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, image: '/analytics_dashboard.png', description: 'KPIs de operación, ventas, mermas y productividad. Dashboards personalizados para cada área del negocio con exportación de datos.', features: ['Operación y ventas en tiempo real', 'Control de mermas', 'Productividad por punto', 'Exportación y alertas'] },
  { id: 'c-suite', label: 'C-Suite', icon: Briefcase, image: '/csuite_dashboard.png', description: 'Dashboard para ejecutivos Level C: ROI por proyecto, métricas financieras, benchmarking y proyecciones de negocio.', features: ['ROI por proyecto', 'Métricas financieras consolidadas', 'Benchmarking sectorial', 'Proyecciones y tendencias'] },
];

export default function HomePage() {
  const [metrics, setMetrics] = useState(null);
  const [logos, setLogos] = useState([]);
  const [solutions, setSolutions] = useState(null);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    Promise.all([
      getMetrics().catch(() => ({ data: [] })),
      getLogos().catch(() => ({ data: [] })),
      getSolutions().catch(() => ({ data: [] })),
      getCases().catch(() => ({ data: [] })),
    ]).then(([m, l, s, c]) => {
      setMetrics(m.data);
      setLogos(l.data);
      setSolutions(s.data);
      setCases(c.data);
    });
  }, []);

  return (
    <div>
      {/* Block 1: Hero */}
      <HeroBlock />
      {/* Block 1.5: Manifesto + ALL IN (2 cols) */}
      <ManifestoBlock />
      {/* Block 2: Trust Bar */}
      <TrustBar logos={logos} />
      {/* Block 3: KPIs */}
      <KPIBlock metrics={metrics} />
      {/* Block 3.5: Industries */}
      <IndustriesBlock />
      {/* Block 3.6: New Products */}
      <NewProductsBlock />
      {/* Block 3.7: Offers/Liquidation */}
      <OffersBlock />
      {/* Block 4: Platform Tabs */}
      <PlatformBlock />
      {/* Block 4.5: QR Demo Section */}
      <QRDemoSection />
      {/* Block 5: Solutions */}
      <SolutionsBlock solutions={solutions} />
      {/* Block 7: Cases */}
      <CasesBlock cases={cases} />
      {/* Block 7.5: Office */}
      <OfficeBlock />
      {/* Block 8: CEO Quote + Crew */}
      <CEOBlock />
      {/* Block 9: Final CTA */}
      <FinalCTABlock />
    </div>
  );
}

// ===== HERO =====

// ===== MANIFESTO + ALL IN (2 columnas) =====
const ManifestoBlock = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });
  return (
    <div
      ref={ref}
      className="bg-white py-12 sm:py-16 lg:py-20"
      data-testid="manifesto-block"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* IMAGEN — izquierda en desktop, arriba en mobile */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.21, 0.61, 0.35, 1] }}
            className="lg:col-span-6 rounded-2xl overflow-hidden shadow-[0_24px_60px_-20px_rgba(2,6,23,0.35)]"
          >
            <img
              src="/brand/allcom-allin-para-ti.png"
              alt="ALL IN para ti — ALL the Support · ALL the Energy · ALL our passion"
              className="w-full h-auto block"
              loading="lazy"
              data-testid="allin-image"
            />
          </motion.div>

          {/* TEXTO — derecha en desktop, abajo en mobile */}
          <div className="lg:col-span-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, ease: [0.21, 0.61, 0.35, 1] }}
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0A2540] tracking-tight mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              Creando el futuro contigo.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.12, ease: [0.21, 0.61, 0.35, 1] }}
              className="text-base sm:text-lg text-[#64748B] leading-relaxed mb-4"
            >
              Toda empresa llega a un punto en que seguir operando igual deja de ser suficiente.
            </motion.p>

            <motion.ul
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2, ease: [0.21, 0.61, 0.35, 1] }}
              className="text-base sm:text-lg text-[#64748B] leading-relaxed mb-4 space-y-1.5"
              data-testid="manifesto-pain-list"
            >
              <li>Los clientes exigen más rapidez.</li>
              <li>Los equipos necesitan mejores herramientas.</li>
              <li>Los datos crecen, pero no siempre se convierten en decisiones.</li>
              <li>Y la tecnología avanza más rápido que muchas organizaciones.</li>
            </motion.ul>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.28, ease: [0.21, 0.61, 0.35, 1] }}
              className="text-base sm:text-lg text-[#0A2540] font-semibold leading-relaxed mb-6"
            >
              En ALLCOM ayudamos a cerrar esa brecha.
            </motion.p>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={inView ? { scaleX: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.36, ease: [0.21, 0.61, 0.35, 1] }}
              className="accent-line w-16 mb-6 origin-left"
            />

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.44, ease: [0.21, 0.61, 0.35, 1] }}
              className="text-sm sm:text-base text-[#64748B] leading-relaxed mb-4"
            >
              Durante más de 20 años hemos acompañado a empresas líderes en Chile con soluciones que combinan inteligencia artificial, software, hardware, integración y soporte operacional. Creamos plataformas personalizadas, automatizamos procesos críticos y transformamos datos en visibilidad ejecutiva.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.52, ease: [0.21, 0.61, 0.35, 1] }}
              className="text-sm sm:text-base text-[#64748B] leading-relaxed mb-4"
            >
              Nuestro propósito es simple: que tu empresa opere con más control, más eficiencia y más capacidad de anticiparse.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.6, ease: [0.21, 0.61, 0.35, 1] }}
              className="text-base sm:text-lg text-[#0A2540] font-medium leading-relaxed italic"
            >
              Porque el futuro no será de quienes tengan más tecnología, sino de quienes sepan usarla mejor.
            </motion.p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== TRUST BAR =====
const TrustBar = ({ logos }) => {
  const { items: dynamicPartners } = useContentList('landing-partners', []);
  const defaultBrands = ['Balanzas Marques', 'Toshiba', 'NCR', 'Wintec POS', 'iMin', 'SOLUM', 'Samsung', 'Lenovo', 'HP', 'OpenAI', 'Anthropic', 'Advantech Taiwan', 'Datalogic', 'Epson', 'POSLAB Taiwan', 'RetailTech USA', 'Justo'];
  const brands = dynamicPartners.length > 0 ? dynamicPartners.map(p => p.name) : defaultBrands;
  return (
    <div className="bg-white border-b border-slate-100 py-6" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs text-center text-slate-400 uppercase tracking-[0.18em] mb-4 font-medium">Partners tecnológicos autorizados</p>
        <Marquee speed={30} pauseOnHover gradient gradientColor="white" gradientWidth={60}>
          {[...brands, ...brands].map((name, i) => (
            <div key={i} className="mx-8 flex items-center">
              <span className="text-sm font-semibold text-slate-300 hover:text-slate-500 transition-colors tracking-wider uppercase" style={{fontFamily:'Inter,sans-serif'}}>{name}</span>
            </div>
          ))}
        </Marquee>
      </div>
    </div>
  );
};

// ===== KPIs (dinámicos desde /api/metrics con fallback) =====
const FALLBACK_KPIS = [
  { label: 'Años de experiencia', value: 20, suffix: '+', prefix: '' },
  { label: 'Proyectos implementados', value: 500, suffix: '+', prefix: '' },
  { label: 'Operación', value: 24, suffix: '/7', prefix: '' },
  { label: 'Interacciones mensuales', value: 20, suffix: ' millones', prefix: '+' },
];

const KPIBlock = ({ metrics }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
  const loading = !metrics; // null/undefined → cargando
  const list = (Array.isArray(metrics) && metrics.length > 0) ? metrics : FALLBACK_KPIS;

  return (
    <Section alt data-testid="kpi-counters">
      <SectionTitle badge="NUESTROS NÚMEROS" title="Cifras que nos respaldan" center />
      <div ref={ref} className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="text-center p-6 rounded-2xl bg-slate-100 animate-pulse h-28" data-testid="kpi-skeleton" />
          ))
        ) : (
          list.slice(0, 4).map((m, i) => {
            const numericValue = Number(m.value);
            const isNumber = !Number.isNaN(numericValue);
            return (
              <motion.div
                key={m.id || m.label || i}
                variants={fadeUp}
                className="text-center p-6 rounded-2xl bg-white border border-slate-100 shadow-sm"
                data-testid={`kpi-card-${i}`}
              >
                <div className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-2" style={{fontFamily:'Syne,sans-serif'}}>
                  {m.prefix || ''}{inView && isNumber ? <CountUp end={numericValue} duration={2} decimals={Number.isInteger(numericValue) ? 0 : 1} /> : (isNumber ? '0' : m.value)}
                  <span className="text-[#0EA5E9] text-lg sm:text-xl">{m.suffix || ''}</span>
                </div>
                <div className="text-sm text-[#64748B] font-medium">{m.label}</div>
              </motion.div>
            );
          })
        )}
      </div>
    </Section>
  );
};

// ===== PLATFORM =====
const PlatformBlock = () => (
  <Section data-testid="platform-showcase">
    <SectionTitle badge="PLATAFORMA SOFTWARE" title="Plataforma ALLCOM" subtitle="Gestión, analytics y visión ejecutiva. Todo desde una sola plataforma." center />
    <Tabs defaultValue="control-panel" className="w-full">
      <TabsList className="flex flex-wrap justify-center gap-2 mb-8 bg-transparent">
        {platformTabs.map(t => {
          const Icon = t.icon;
          return (
            <TabsTrigger key={t.id} value={t.id} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-slate-200 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white data-[state=active]:border-[#0A2540] hover:bg-slate-50 transition-colors" data-testid={`platform-tab-${t.id}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </TabsTrigger>
          );
        })}
      </TabsList>
      {platformTabs.map(t => (
        <TabsContent key={t.id} value={t.id}>
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl bg-[#0A2540]">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0A2540] border-b border-white/10">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                <span className="ml-2 text-[10px] text-slate-500 font-mono">ALLCOM {t.label}</span>
              </div>
              <img src={t.image} alt={`ALLCOM ${t.label}`} className="w-full h-64 sm:h-80 object-cover object-top" loading="lazy" style={t.id === 'ceo-cockpit' ? { filter: 'blur(1.5px) brightness(0.95)' } : {}} />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-3" style={{fontFamily:'Syne,sans-serif'}}>{t.label}</h3>
              <p className="text-[#64748B] mb-6 leading-relaxed">{t.description}</p>
              <ul className="space-y-3">
                {t.features.map((f,i) => (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] shrink-0 bg-[#0EA5E9]">{i+1}</div>
                    <span className="text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>
              <Link to={`/software/${t.id}`} className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#0EA5E9] hover:underline">
                Ver más <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </TabsContent>
      ))}
    </Tabs>
  </Section>
);

// ===== SOLUTIONS (dinámico desde /api/solutions) =====
const SolutionsBlock = ({ solutions }) => {
  const loading = solutions === null || solutions === undefined;
  const list = Array.isArray(solutions) ? solutions : [];

  return (
    <Section alt data-testid="solutions-block">
      <SectionTitle badge="SOLUCIONES" title="Soluciones integrales para tu industria" subtitle="Hardware + software + operación 24/7 en un solo proveedor." center />
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="solutions-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-slate-100 animate-pulse h-48" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="text-center text-slate-400 text-sm py-12" data-testid="solutions-empty">
          No hay soluciones publicadas todavía.
        </div>
      ) : (
        <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="solutions-grid">
          {list.map((s, idx) => {
            const Icon = solutionIcons[s.slug] || Monitor;
            const title = s.title || s.h1 || s.slug;
            const desc = s.subtitle || s.description || '';
            return (
              <motion.div key={s.id || s.slug || idx} variants={fadeUp}>
                <Link
                  to={`/soluciones/${s.slug}`}
                  className="group block p-6 sm:p-8 rounded-2xl bg-white border border-slate-200 shadow-[0_10px_30px_rgba(2,6,23,0.08)] card-hover h-full text-center"
                  data-testid={`solution-card-${s.slug}`}
                >
                  <div className="w-12 h-12 mx-auto rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center mb-4 group-hover:bg-[#0EA5E9] group-hover:border-[#0EA5E9] transition-colors">
                    <Icon className="w-6 h-6 text-[#0EA5E9] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-semibold mb-2 group-hover:text-[#0EA5E9] transition-colors" style={{ fontFamily: 'Syne,sans-serif' }}>{title}</h3>
                  <p className="text-sm text-[#64748B] leading-relaxed">{desc}</p>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </Section>
  );
};

// ===== INDUSTRIES =====
// ===== NEW PRODUCTS =====
const NewProductsBlock = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch latest 6 products (backend ordena por display_order ASC, created_at DESC)
    // Preferimos ?limit=6 para tener un bloque compacto.
    axios.get(`${API}/products?limit=6`)
      .then((r) => {
        // Doble-orden por created_at desc para asegurar que los más nuevos salen primero
        const sorted = (r.data || [])
          .slice()
          .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
          .slice(0, 6);
        setProducts(sorted);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const resolveImg = (p) => {
    const url = p.image || (Array.isArray(p.images) && p.images[0]?.url) || '/placeholder-product.png';
    return url?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  return (
    <Section alt>
      <SectionTitle
        badge="NUEVOS PRODUCTOS"
        title="Recién llegados"
        subtitle="Los últimos equipos disponibles en Chile — se actualizan automáticamente al crear un producto en el admin."
        center
      />
      {loading ? (
        <div className="text-center text-slate-400 text-sm py-12">Cargando últimos lanzamientos…</div>
      ) : products.length === 0 ? (
        <div className="text-center text-slate-400 text-sm py-12">Aún no hay productos recientes.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto" data-testid="new-products-grid">
          {products.map((product) => (
            <motion.div
              key={product.id || product.slug}
              variants={fadeUp}
              className="group relative bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl hover:border-[#0EA5E9]/30 transition-all duration-300"
              data-testid={`new-product-${product.slug}`}
            >
              <div className="absolute top-4 right-4 z-10">
                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider bg-[#0EA5E9] text-white rounded-full shadow-lg">
                  Nuevo
                </span>
              </div>
              <div className="aspect-square bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden flex items-center justify-center">
                <img
                  src={resolveImg(product)}
                  alt={`${product.brand || ''} ${product.name}`}
                  className="w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
              <div className="p-5 text-center">
                <div className="flex items-center gap-2 mb-2 justify-center">
                  {product.brand && (
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#B56DFF' }}>
                      {product.brand}
                    </span>
                  )}
                  {product.category_display && (
                    <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full font-medium">{product.category_display}</span>
                  )}
                </div>
                <h3 className="text-base font-semibold text-[#0A2540] mb-2 group-hover:text-[#0EA5E9] transition-colors" style={{ fontFamily: 'Syne,sans-serif' }}>
                  {product.name}
                </h3>
                {product.summary && (
                  <p className="text-xs text-[#64748B] leading-relaxed mb-3">{product.summary}</p>
                )}
                <Link
                  to={`/hardware/${product.category}/${product.slug}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0EA5E9] hover:gap-2 transition-all"
                >
                  Ver más <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      {/* Design Award Badge */}
      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 max-w-2xl mx-auto">
        <img src="/reddot_award_2025.webp" alt="Red Dot Design Award 2025 Winner" className="h-16 sm:h-20 object-contain" />
        <div className="text-center sm:text-left">
          <p className="text-sm font-semibold text-[#0A2540]" style={{ fontFamily: 'Syne,sans-serif' }}>Ganadores Design Award 2025</p>
          <p className="text-xs text-slate-500">Reconocimiento internacional por innovación y excelencia en diseño</p>
        </div>
      </div>
    </Section>
  );
};


// ===== AI ASSISTANT =====
const AIAssistantBlock = () => {
  const [chatMessage, setChatMessage] = useState('');
  const exampleQuestions = [
    `Tengo 15 tiendas retail, que solucion me recomiendas`,
    `Cuanto cuesta implementar autoservicio en 8 sucursales`,
    `Necesito ESL para 3 supermercados, cual es el ROI`,
    `Como puedo reducir filas en mis tiendas`,
  ];

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#0E3A5F] py-16 sm:py-20 lg:py-24">
      {/* Accent glow */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20" style={{
        background: 'radial-gradient(circle, rgba(14,165,233,0.4) 0%, transparent 70%)'
      }} />
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full opacity-20" style={{
        background: 'radial-gradient(circle, rgba(1,145,180,0.4) 0%, transparent 70%)'
      }} />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 mb-4">
            <Zap className="w-4 h-4 text-[#0EA5E9]" strokeWidth={1.5} />
            <span className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider">IA Avanzada</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily:'Syne,sans-serif'}}>
            Habla con nuestro Asistente IA
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Cuéntanos sobre tu operación y te generamos un dashboard ejecutivo personalizado con KPIs, recomendaciones de productos y proyección de ROI.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Left: Chat Interface */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            {/* Chat Container */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden">
              {/* Chat Header */}
              <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-[#0EA5E9] to-[#0191B4] border-b border-[#0EA5E9]/20">
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-white" style={{fontFamily:'Syne,sans-serif'}}>Asistente ALLCOM IA</h4>
                  <p className="text-xs text-sky-100">Online · Responde en segundos</p>
                </div>
                <div className="ml-auto flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                </div>
              </div>

              {/* Chat Messages */}
              <div className="p-5 space-y-4 h-64 overflow-y-auto bg-slate-50">
                {/* AI Message */}
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#0191B4] flex items-center justify-center shrink-0">
                    <Zap className="w-4 h-4 text-white" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <div className="bg-white rounded-2xl rounded-tl-none p-4 shadow-sm border border-slate-200">
                      <p className="text-sm text-slate-700 leading-relaxed">
                        👋 Hola, soy el asistente IA de ALLCOM. <br/>
                        <br/>
                        Cuéntame sobre tu operación y te ayudaré a encontrar la mejor solución:
                        <br/><br/>
                        • ¿Qué industria? (Retail, HORECA, Salud, etc.)<br/>
                        • ¿Cuántas sucursales tienes?<br/>
                        • ¿Qué desafío quieres resolver?
                      </p>
                    </div>
                    <span className="text-[10px] text-slate-400 ml-2 mt-1 inline-block">Hace un momento</span>
                  </div>
                </div>
              </div>

              {/* Input */}
              <div className="p-4 bg-white border-t border-slate-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    placeholder="Escribe tu consulta aquí..."
                    className="flex-1 px-4 py-3 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                  />
                  <button className="px-5 py-3 bg-gradient-to-r from-[#0EA5E9] to-[#0191B4] text-white rounded-xl hover:shadow-lg transition-all flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            </div>

            {/* Example Questions */}
            <div className="mt-4 space-y-2">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Preguntas frecuentes:</p>
              {exampleQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => setChatMessage(q)}
                  className="block w-full text-left px-4 py-2.5 text-xs text-slate-300 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-[#0EA5E9]/30 transition-all"
                >
                  💬 {q}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Right: Preview Dashboard */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-6">
              <div className="flex items-center gap-2 mb-4">
                <LayoutDashboard className="w-5 h-5 text-[#0EA5E9]" strokeWidth={1.5} />
                <h4 className="text-sm font-semibold text-white" style={{fontFamily:'Syne,sans-serif'}}>Dashboard que generarás</h4>
              </div>
              
              <div className="space-y-3">
                {[
                  { icon: Target, label: 'Análisis de tu operación', desc: 'Evaluación de capacidad, flujo y puntos críticos' },
                  { icon: ShoppingCart, label: 'Equipos recomendados', desc: 'Hardware específico para tu industria y escala' },
                  { icon: TrendingUp, label: 'Proyección ROI', desc: 'Retorno de inversión proyectado a 12, 24 y 36 meses' },
                  { icon: BarChart3, label: 'KPIs esperados', desc: 'Reducción de filas, aumento ventas, eficiencia' },
                  { icon: Clock, label: 'Cronograma de implementación', desc: 'Timeline realista de instalación y puesta en marcha' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                      <Icon className="w-5 h-5 text-[#0EA5E9] shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div>
                        <h5 className="text-sm font-semibold text-white mb-0.5">{item.label}</h5>
                        <p className="text-xs text-slate-400 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-[#0EA5E9]/10 to-[#0191B4]/10 border border-[#0EA5E9]/20">
                <p className="text-xs text-slate-300 leading-relaxed">
                  <span className="font-semibold text-[#0EA5E9]">✨ Generado con IA en menos de 2 minutos.</span> Análisis basado en 469 proyectos ejecutados y 19+ años de experiencia ALLCOM.
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#0EA5E9]" style={{fontFamily:'Syne,sans-serif'}}>{'<'}2min</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Análisis completo</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#F8D90F]" style={{fontFamily:'Syne,sans-serif'}}>100%</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Personalizado</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#0191B4]" style={{fontFamily:'Syne,sans-serif'}}>PDF</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Reporte ejecutivo</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};




// LIVE DASHBOARD - Movido a /empresa/operacion-en-vivo
// Ver: /app/frontend/src/pages/OperacionEnVivoPage.js


// ===== CASES =====
const CasesBlock = ({ cases }) => {
  if (cases.length === 0) return null;
  return (
    <Section alt>
      <SectionTitle badge="CASOS DE ÉXITO" title="Resultados que hablan" subtitle="Proyectos reales con métricas verificables." />
      <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {cases.slice(0, 3).map((c) => (
          <motion.div key={c.id || c.slug} variants={fadeUp}>
            <div className="p-6 sm:p-8 rounded-2xl bg-white border border-slate-200 shadow-[0_10px_30px_rgba(2,6,23,0.08)] card-hover h-full flex flex-col">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#0EA5E9] mb-3">{c.industry}</span>
              <h3 className="text-base font-semibold mb-3" style={{fontFamily:'Syne,sans-serif'}}>{c.title}</h3>
              <p className="text-sm text-[#64748B] mb-4 flex-1">{c.summary}</p>
              {c.metrics && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {c.metrics.map((m, i) => (
                    <div key={i} className="text-center p-2 rounded-lg bg-slate-50">
                      <div className="text-sm font-bold text-[#0A2540]">{m.value}</div>
                      <div className="text-[10px] text-[#64748B]">{m.label}</div>
                    </div>
                  ))}
                </div>
              )}
              {c.quote && (
                <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-xl">
                  <Quote className="w-4 h-4 text-[#0EA5E9] shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-600 italic leading-relaxed">{c.quote}</p>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </motion.div>
      <div className="mt-8 text-center">
        <Link to="/casos" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0EA5E9] hover:underline">
          Ver todos los casos <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Section>
  );
};


// ===== OFFICE =====
const OfficeBlock = () => (
  <Section>
    <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-testid="office-block">
      <img
        src="/allcom_oficina_providencia_santiago_chile.jpg"
        alt="Oficina central ALLCOM - Andrés Bello 1567, Providencia, Santiago, Chile"
        title="ALLCOM Oficina Central · Providencia · Santiago · Chile"
        className="w-full h-64 sm:h-80 lg:h-[420px] object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050E1F]/80 via-[#050E1F]/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
        <div className="max-w-2xl">
          <h3 className="text-xl sm:text-2xl font-semibold text-white mb-2" style={{fontFamily:'Syne,sans-serif'}}>Nuestra oficina central</h3>
          <p className="text-sm text-slate-300 mb-1">Andrés Bello 1567 · Providencia · Santiago · Chile</p>
          <p className="text-xs text-slate-400">Donde la tecnología se diseña, se prueba y se despliega para todo Chile.</p>
        </div>
      </div>
    </div>
  </Section>
);

// ===== CEO SECTION =====
const CEOBlock = () => (
  <Section dark>
    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start" data-testid="ceo-section">
      {/* CEO Photo (B&W, conservada) */}
      <motion.div variants={fadeUp} className="lg:col-span-5 flex justify-center lg:justify-start">
        <div className="relative">
          <div className="w-64 h-80 sm:w-72 sm:h-96 lg:w-full lg:h-[520px] rounded-2xl overflow-hidden shadow-2xl border border-white/10">
            <img
              src="/ceo-mario.jpg"
              alt="Mario Pino Corona — CEO & Fundador, ALLCOM"
              className="w-full h-full object-cover object-top"
              style={{ filter: 'grayscale(100%)' }}
            />
          </div>
          {/* Sutil acento monocromo */}
          <div className="absolute -bottom-3 -right-3 w-20 h-20 rounded-xl bg-[#0EA5E9]/15 -z-10" />
        </div>
      </motion.div>

      {/* CEO Info — derecha */}
      <motion.div variants={fadeUp} className="lg:col-span-7">
        {/* Etiqueta */}
        <span
          className="inline-flex items-center gap-2 px-3 py-1 mb-8 text-[10px] font-mono font-semibold uppercase tracking-[0.32em] text-[#0EA5E9] bg-sky-500/10 border border-sky-500/20 rounded-full"
          data-testid="ceo-tag"
        >
          ALL IN PARA TI
        </span>

        {/* Frase manifiesto grande */}
        <blockquote
          className="text-2xl sm:text-3xl lg:text-4xl font-semibold leading-[1.2] text-white mb-8"
          style={{ fontFamily: 'Syne, sans-serif' }}
          data-testid="ceo-quote"
        >
          “En Allcom no vendemos cajas. Nos hacemos cargo de operaciones críticas.”
        </blockquote>

        {/* Bajada emocional */}
        <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-10 max-w-2xl">
          Acompañamos a nuestros clientes con tecnología, soporte en terreno, criterio técnico y compromiso humano, para que cada proyecto funcione donde realmente importa: en la operación diaria.
        </p>

        {/* Firma */}
        <div className="mb-12 pb-10 border-b border-white/10">
          <div className="text-lg font-semibold text-white mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>Mario Pino Corona</div>
          <div className="text-sm text-[#0EA5E9] font-medium mb-2">CEO &amp; Fundador, Allcom</div>
          <a
            href="https://www.marioandres.cl"
            target="_blank"
            rel="me noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#0EA5E9] transition-colors"
            data-testid="ceo-website-link"
          >
            <ExternalLink className="w-3.5 h-3.5" /> www.marioandres.cl
          </a>
        </div>

        {/* Subtítulo cards */}
        <h4
          className="text-[11px] font-mono font-semibold uppercase tracking-[0.32em] text-slate-400 mb-6"
          data-testid="ceo-cards-subtitle"
        >
          Experiencia que se transforma en confianza
        </h4>

        {/* 4 cards con iconos sólidos cyan */}
        <div className="grid sm:grid-cols-2 gap-4" data-testid="ceo-cards-grid">
          {[
            {
              icon: ShieldCheck,
              title: 'Pioneros en autoservicio en Chile',
              desc: 'Fuimos parte de las primeras implementaciones de autoservicio y self-checkout en grandes cadenas de retail, transformando la experiencia de compra y la eficiencia operacional.',
            },
            {
              icon: TrendingUp,
              title: 'Tecnología con impacto real',
              desc: 'Hemos ayudado a generar ahorros relevantes para la industria mediante automatización, eficiencia operativa y mejores procesos en tienda.',
            },
            {
              icon: MapPin,
              title: 'Más de 500 aperturas implementadas',
              desc: 'Hemos participado en aperturas, rollouts y despliegues tecnológicos en tiendas, supermercados y operaciones críticas a nivel nacional.',
            },
            {
              icon: Cpu,
              title: 'IA aplicada al retail real',
              desc: 'Integramos inteligencia artificial para resolver problemas concretos: detección de fraude, control operacional, soporte a decisiones y mejora de experiencia en autoservicio.',
            },
          ].map((card, i) => {
            const Icon = card.icon;
            return (
              <div
                key={i}
                className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#0EA5E9]/40 transition-colors"
                data-testid={`ceo-card-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[#0EA5E9]" strokeWidth={1.6} />
                </div>
                <div>
                  <h5 className="text-sm font-semibold text-white mb-1.5 leading-snug" style={{ fontFamily: 'Syne, sans-serif' }}>{card.title}</h5>
                  <p className="text-xs text-slate-400 leading-relaxed">{card.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  </Section>
);

// ===== FINAL CTA =====
const FinalCTABlock = () => (
  <Section>
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <motion.div variants={fadeUp}>
        <SectionTitle badge="COMIENZA HOY" title="Transforma tu operación con tecnología real" subtitle="Compléta el formulario y agenda una demo gratuita con nuestro equipo. Sin compromiso." />
        <div className="flex flex-col sm:flex-row gap-4 text-sm text-[#64748B]">
          <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#10B981]" /> Sin compromiso</div>
          <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#10B981]" /> Demo personalizada</div>
          <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#10B981]" /> Respuesta en 24h</div>
        </div>
      </motion.div>
      <motion.div variants={fadeUp}>
        <div className="p-6 sm:p-8 rounded-2xl bg-white border border-slate-200 shadow-[0_10px_30px_rgba(2,6,23,0.08)]">
          <h3 className="text-lg font-semibold mb-4" style={{fontFamily:'Syne,sans-serif'}}>Agenda tu demo gratuita</h3>
          <MiniForm sourcePage="/" />
        </div>
      </motion.div>
    </div>
  </Section>
);
